import { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import useScrollReveal from "../useScrollReveal";
import useSectionAnalytics, { mergeRefs } from "../useSectionAnalytics";
import { useLang } from "../LanguageContext";

// Numbers are identical across languages — not translated content.
const CARD_NUMBERS = ["01", "02", "03", "04"];

export default function CoreValues() {
  const [ref, visible] = useScrollReveal();
  const analyticsRef = useSectionAnalytics("core_values");
  const [active, setActive] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const tagRef = useRef(null);
  const rafRef = useRef(null);
  const rotationRef = useRef(0);
  const lastTimeRef = useRef(null);
  const { lang } = useLang();
  const { t } = useTranslation();
  const tx = {
    section_tag: t("coreValues.sectionTag"),
    section_title: t("coreValues.sectionTitle"),
    cards: t("coreValues.cards", { returnObjects: true }).map((card, i) => ({
      ...card,
      number: CARD_NUMBERS[i],
    })),
  };

  const RADIUS = 180;
  const CENTER = 220;
  const TOTAL = tx.cards.length;
  const SPEED = 0.006;

  // Smooth rotation — clockwise (negative direction fix)
  useEffect(() => {
    if (!visible) return;

    const animate = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      if (!userInteracted) {
        // Clockwise = subtract rotation so points move clockwise
        rotationRef.current = (rotationRef.current + SPEED * delta) % 360;

        setRotation(rotationRef.current);
        // Auto-select nearest point
        const anglePerItem = 360 / TOTAL;
        const normalizedRotation = ((rotationRef.current % 360) + 360) % 360;
        const nearest =  Math.round(normalizedRotation / anglePerItem) % TOTAL;
        setActive(nearest % TOTAL);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible, userInteracted, TOTAL]);

  // GSAP header
  useEffect(() => {
    if (!visible || !tagRef.current) return;
    gsap.fromTo(
      tagRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
    );
  }, [visible]);

  const handleClick = useCallback((i) => {
  setActive(i);
  setUserInteracted(true);

  const anglePerItem = 360 / TOTAL;
  const targetRotation = i * anglePerItem;

  gsap.to(rotationRef, {
    current: targetRotation,
    duration: 0.8,
    ease: "power2.inOut",
    onUpdate: () => setRotation(rotationRef.current),
  });
}, [TOTAL]);

  // Resume auto-rotate after 6s
  useEffect(() => {
    if (!userInteracted) return;
    const timer = setTimeout(() => {
      setUserInteracted(false);
      lastTimeRef.current = null;
    }, 6000);
    return () => clearTimeout(timer);
  }, [userInteracted, active]);

  const getPoint = (i) => {
    const baseAngle = (-i / TOTAL) * 2 * Math.PI - Math.PI / 2;
    const rotRad = (rotation * Math.PI) / 180;
    const angle = baseAngle + rotRad;
    return {
      x: CENTER + RADIUS * Math.cos(angle),
      y: CENTER + RADIUS * Math.sin(angle),
      angle,
    };
  };

  // Label position offset — push label away from center
  const getLabelOffset = (pt) => {
    const dx = pt.x - CENTER;
    const dy = pt.y - CENTER;
    const len = Math.sqrt(dx * dx + dy * dy);
    const nx = dx / len;
    const ny = dy / len;
    const push = 36;
    return {
      x: pt.x + nx * push,
      y: pt.y + ny * push,
      anchor: nx < -0.3 ? "end" : nx > 0.3 ? "start" : "middle",
    };
  };

  const activeCard = tx.cards[active];

  return (
    <section ref={mergeRefs(ref, analyticsRef)} className={`core-values ${visible ? "visible" : ""}`}>

      {/* Header */}
      <div className="core-values__header">
        <span ref={tagRef} className="core-values__tag" style={{ opacity: 0 }}>
          {tx.section_tag}
        </span>
        <h2 className="core-values__title">{tx.section_title}</h2>
      </div>

      {/* Layout — circle LEFT, content RIGHT */}
      <div className="core-values__layout">

        {/* LEFT — Circle */}
        <div className="core-values__circle-wrap">
          <svg
            width={CENTER * 2}
            height={CENTER * 2}
            viewBox={`0 0 ${CENTER * 2} ${CENTER * 2}`}
            className="core-values__svg"
          >
            {/* Outer ring */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="var(--brand-gradient-light)"
              strokeWidth="1.5"
            />

            {/* Inner dashed ring */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS * 0.55}
              fill="none"
              stroke="var(--brand-gradient-light)"
              strokeWidth="1"
              strokeDasharray="3 10"
            />

            {/* Center */}
            <circle cx={CENTER} cy={CENTER} r={28} fill="var(--brand-secondary)" opacity={0.05} />
            <circle cx={CENTER} cy={CENTER} r={4} fill="var(--brand-secondary)" opacity={0.25} />

            {/* Connector lines */}
            {tx.cards.map((_, i) => {
              const pt = getPoint(i);
              const isActive = i === active;
              return (
                <line
                  key={i}
                  x1={CENTER} y1={CENTER}
                  x2={pt.x} y2={pt.y}
                  stroke="var(--brand-gradient-dark)"
                  strokeWidth={isActive ? 1 : 0.3}
                  opacity={isActive ? 0.45 : 0.1}
                  style={{ transition: "opacity 0.4s ease" }}
                />
              );
            })}

            {/* Points + Labels */}
            {tx.cards.map((card, i) => {
              const pt = getPoint(i);
              const label = getLabelOffset(pt);
              const isActive = i === active;
              const shortLines = card.short.split("\n");

              return (
                <g
                  key={i}
                  onClick={() => handleClick(i)}
                  style={{ cursor: "pointer" }}
                >
                  {/* Pulse for active */}
                  {isActive && (
                    <circle cx={pt.x} cy={pt.y} r={20} fill="none"
                      stroke="var(--brand-secondary)" strokeWidth="1" opacity="0.2"
                    >
                      <animate attributeName="r" values="16;26;16" dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.3;0;0.3" dur="2.5s" repeatCount="indefinite" />
                    </circle>
                  )}

                  {/* Dot */}
                  <circle
                    cx={pt.x} cy={pt.y}
                    r={isActive ? 14 : 7}
                    fill={isActive ? "var(--brand-secondary)" : "white"}
                    stroke="var(--brand-secondary)"
                    strokeWidth={isActive ? 0 : 1}
                    opacity={isActive ? 1 : 0.4}
                    style={{ transition: "r 0.4s ease, opacity 0.4s ease, fill 0.4s ease" }}
                  />

                  {/* Number inside active dot */}
                  {isActive && (
                    <text
                      x={pt.x} y={pt.y + 4}
                      textAnchor="middle"
                      fontSize="9" fontFamily="Inter, sans-serif"
                      fontWeight="500" fill="white" letterSpacing="0.05em"
                      style={{ pointerEvents: "none" }}
                    >
                      {card.number}
                    </text>
                  )}

                  {/* Short label — always visible */}
                  {shortLines.map((line, li) => (
                    <text
                      key={li}
                      x={label.x}
                      y={label.y + li * 13 - (shortLines.length - 1) * 6}
                      textAnchor={label.anchor}
                      fontSize={isActive ? "10.5" : "9"}
                      fontFamily="Inter, sans-serif"
                      fontWeight={isActive ? "600" : "400"}
                      fill={isActive ? "var(--brand-secondary)" : "#bbb"}
                      letterSpacing="0.06em"
                      style={{
                        transition: "fill 0.4s ease, font-size 0.4s ease",
                        pointerEvents: "none",
                        textTransform: "uppercase",
                      }}
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            })}
          </svg>
        </div>

        {/* RIGHT — Content */}
        <div className="core-values__content">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${active}-${lang}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="core-values__content-inner"
            >
              <span className="core-values__number">{activeCard.number}</span>
              <h3 className="core-values__card-title">{activeCard.title}</h3>
              <p className="core-values__card-desc">{activeCard.desc}</p>
              <span className="core-values__highlight">{activeCard.highlight}</span>
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
}